import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Calculator, GitCompare, FolderOpen, Beaker, Compass, Activity, Flame, MapPin, Box, BookOpen,
  DollarSign, TrendingUp, Ruler, ShieldCheck,
} from "lucide-react";
import HeaderBar from "@/components/portal/HeaderBar";
import LayerBuilder from "@/components/portal/LayerBuilder";
import MaterialLibrary from "@/components/portal/MaterialLibrary";
import MetricsPanel from "@/components/portal/MetricsPanel";
import GlaserChart from "@/components/portal/GlaserChart";
import AIAdvicePanel from "@/components/portal/AIAdvicePanel";
import ExportBar from "@/components/portal/ExportBar";
import WallSectionDrawing from "@/components/portal/WallSectionDrawing";
import CompareView from "@/components/portal/CompareView";
import ProjectsPanel from "@/components/portal/ProjectsPanel";
import AcademicView from "@/components/portal/AcademicView";
import SmartAdvisor from "@/components/portal/SmartAdvisor";
import GlaserPage from "@/components/portal/GlaserPage";
import StandardsBar from "@/components/portal/StandardsBar"; // eslint-disable-line no-unused-vars
import FireRetardantsView from "@/components/portal/FireRetardantsView";
import WizardBar from "@/components/portal/WizardBar";
import DarkSuppliersMap from "@/components/portal/DarkSuppliersMap";
import InteractiveSuppliersMap from "@/components/portal/InteractiveSuppliersMap";
import MaterialDetailView from "@/components/portal/MaterialDetailView";
import LibraryView from "@/components/portal/LibraryView";
import EkonomiView from "@/components/portal/EkonomiView";
import MaliyetView from "@/components/portal/MaliyetView";
import BoyutlarView from "@/components/portal/BoyutlarView";
import YogusmaView from "@/components/portal/YogusmaView";
import MaterialDashboardView from "@/components/portal/MaterialDashboardView";
import TS825View from "@/components/portal/TS825View";
import { useI18n } from "@/lib/i18n";
import { useWizard } from "@/lib/wizard";

const TAB_DEFS = [
  { id: "calc",      labelKey: "tab.calc",      icon: Calculator,  color: "#00E5FF", iconColor: "#FF8C00" }, // Orange
  { id: "material",  labelKey: "tab.material",  icon: Box,         color: "#00E5FF", iconColor: "#FF2DAA" }, // Pink
  { id: "standards", labelKey: "tab.standards", icon: ShieldCheck, color: "#00E5FF", iconColor: "#FFD700" }, // Yellow
  { id: "rd",        labelKey: "tab.rd",        icon: Beaker,      color: "#00E5FF", iconColor: "#B45CFF" }, // Purple
  { id: "compare",   labelKey: "tab.compare",   icon: GitCompare,  color: "#00E5FF", iconColor: "#B6FF00" }, // Lime Green
  { id: "advisor",   labelKey: "tab.advisor",   icon: Compass,     color: "#00E5FF", iconColor: "#FFB300" }, // Gold/Amber
  { id: "library",   labelKey: "tab.library",   icon: BookOpen,    color: "#00E5FF", iconColor: "#FF00FF" }, // Magenta
  { id: "projects",  labelKey: "tab.projects",  icon: FolderOpen,  color: "#00E5FF", iconColor: "#5B7CFF" }, // Indigo/Blue
];

export default function HeatPortal() {
  const { t, standardData } = useI18n();
  const { material: selectedMaterial } = useWizard();
  const [tab, setTab] = useState("calc");
  const [materialSubTab, setMaterialSubTab] = useState("izolasyon");
  const [showStandardsRow, setShowStandardsRow] = useState(false);

  // ── Row 3 (technical standards) — toggled by Row 2 "standarts" button ──
  const STANDARDS_ROW3 = [
    { id: "TR",       label: "TS 825:2024",     url: "https://intweb.tse.org.tr/Standard/Standard/Standard.aspx?081118051115108051104119110104055047105102120088111043113104073120085", color: "#00E5FF" },
    { id: "EU",       label: "ISO 6946",        url: "https://www.iso.org/standard/65708.html", color: "#39FF14" },
    { id: "US",       label: "ASTM C518",       url: "https://www.astm.org/c0518-21.html", color: "#FF8C42" },
    { id: "DIN",      label: "DIN 4108 / 4102", url: "https://www.din.de/en/getting-involved/standards-committees/nabau/publications/wdc-beuth:din21:296298496", color: "#FFCC00" },
    { id: "EPS_EN",   label: "TS EN 13163",     url: "https://standards.iteh.ai/catalog/standards/cen/d35ed5e9-d56d-4a8e-a89a-32eaa01bb612/en-13163-2012-a2-2016", color: "#1E40AF" },
    { id: "GLASER",   label: "GLASER",          url: "https://www.iso.org/standard/51615.html", color: "#EC4899", internalTab: "calc" },
    { id: "AI",       label: "AI",              url: "#ai", color: "#A855F7", internalTab: "smart-advisor" },
  ];

  // HeaderBar controls body bg/color via the dark/light <html> class.
  // No body styling needed here.

  // Climate state for the calc tab (still TS 825 by default)
  const [climate] = useState({
    city: "Ankara", district: "Çankaya", zone: 3, hdd: 2716, cdd: 358,
    climate_zone_label: "TS 825 - Zone 3",
  });

  const [layers, setLayers] = useState([
    { id: crypto.randomUUID(), name: "Gypsum Plaster",            thickness: 0.02, lambda_value: 0.51,  mu: 10 },
    { id: crypto.randomUUID(), name: "Horizontal Perforated Brick", thickness: 0.19, lambda_value: 0.45, mu: 8 },
    { id: crypto.randomUUID(), name: "Stone Wool Slab",           thickness: 0.05, lambda_value: 0.038, mu: 1 },
    { id: crypto.randomUUID(), name: "EIFS Top Coat (Acrylic)",    thickness: 0.01, lambda_value: 0.70, mu: 20 },
  ]);

  const [conditions, setConditions] = useState({
    indoor_temp: 22, outdoor_temp: 20, indoor_humidity: 50, outdoor_humidity: 60,
    _mode: "day",
  });
  const [materials, setMaterials] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);

  const loadMaterials = useCallback(async () => {
    try { const { data } = await api.get("/materials"); setMaterials(data); }
    catch { toast.error("Failed to load materials"); }
  }, []);
  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  useEffect(() => {
    const tm = setTimeout(async () => {
      try {
        const { data } = await api.post("/calculate", {
          layers: layers.map(({ name, thickness, lambda_value, mu }) =>
            ({ name, thickness, lambda_value, mu: mu ?? 1 })),
          ...conditions,
        });
        setMetrics(data);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(tm);
  }, [layers, conditions]);

  // Auto-jump to Material tab when a material is selected via WizardBar
  useEffect(() => {
    if (selectedMaterial && tab === "calc") setTab("material");
    // eslint-disable-next-line
  }, [selectedMaterial]);

  const requestAdvice = async () => {
    if (layers.length === 0) return toast.error("Add at least one layer");
    setAdviceLoading(true);
    setAdvice(null);
    try {
      const { data } = await api.post("/expert-advice", {
        layers: layers.map(({ name, thickness, lambda_value, mu }) =>
          ({ name, thickness, lambda_value, mu: mu ?? 1, brand: "" })),
        total_r: Number(metrics.total_r.toFixed(4)),
        u_value: Number(metrics.u_value.toFixed(4)),
        climate_zone: "TS 825 - Zone 3",
        building_element: "External Wall",
      });
      setAdvice(data);
      toast.success("AI analysis ready");
    } catch (e) {
      toast.error("AI failure: " + (e.response?.data?.detail || e.message));
    } finally { setAdviceLoading(false); }
  };

  const getCurrentSnapshot = () => ({
    project_name: "", customer: "", engineer: "", date: new Date().toISOString().slice(0,10),
    building_element: "External Wall",
    climate_zone: "TS 825 - Zone 3",
    climate,
    layers: layers.map(({ name, thickness, lambda_value, mu }) =>
      ({ name, thickness, lambda_value, mu: mu ?? 1, brand: "" })),
    conditions, metrics: metrics || {}, advice,
  });

  const applySnapshot = (s) => {
    if (s.conditions) setConditions(s.conditions);
    if (s.layers) setLayers(s.layers.map((l) => ({ id: crypto.randomUUID(), ...l })));
    if (s.advice) setAdvice(s.advice);
    setTab("calc");
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: conditions?._mode === "day" ? "#FFFFFF" : "#000000",
        color: conditions?._mode === "day" ? "#0F172A" : "#FFFFFF",
      }}>
      <HeaderBar conditions={conditions} setConditions={setConditions}/>

      {/* Wizard navigation bar */}
      <WizardBar onNavigate={(target) => {
        if (target === "material")  setTab("material");
        if (target === "library")   setTab("library");
        if (target === "ar-ge")     setTab("rd");
        if (target === "ekonomi")   setTab("finans");
        if (target === "boyutlar")  setTab("boyutlar");
        if (target === "maliyet")   setTab("maliyet");
      }}/>

      <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1600px] mx-auto">
        {/* Tabs — mutually exclusive active state (only ONE tab purple at a time) */}
        <div className="border border-[#1F1F1F] bg-black flex items-center overflow-x-auto" data-testid="tabs">
          {TAB_DEFS.map((tt) => {
            const Icon = tt.icon;
            const isStandardsToggle = tt.id === "standards";
            // Unified active ID: standards row (when open) wins over tab state.
            // Guarantees only ONE tab renders as active at any moment.
            const activeVisualId = showStandardsRow ? "standards" : tab;
            const active = activeVisualId === tt.id;
            const isLight = conditions?._mode === "day";
            // Unified single-color Row 2 scheme:
            //   GÜNDÜZ (light) → deep royal blue (#1E40AF) on white tab-bar
            //   GECE  (night) → pure white (#FFFFFF) on black tab-bar
            // Icons take the SAME color as the label (no rainbow scheme).
            const baseColor = isLight ? "#1E40AF" : "#FFFFFF";
            const activeBg = isLight ? "#1E40AF" : tt.color;
            const activeFg = isLight ? "#FFFFFF" : "#000000";
            const textColor = active ? activeFg : baseColor;
            const iconColor = active ? activeFg : baseColor;
            const onClick = () => {
              if (isStandardsToggle) {
                // Toggle the standards row. When opening it becomes the exclusive active state
                // via activeVisualId; when closing, previous `tab` value takes over again.
                setShowStandardsRow((v) => !v);
              } else {
                // Any real-tab click closes the standards row and activates only this tab.
                setShowStandardsRow(false);
                setTab(tt.id);
              }
            };
            const hoverBg = isLight ? "rgba(30, 64, 175, 0.08)" : "#0F0F0F";
            return (
              <button key={tt.id} onClick={onClick}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = hoverBg; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
                style={{
                  background: active ? activeBg : "transparent",
                  color: textColor,
                  borderRight: "1px solid var(--row2-border, #1F1F1F)",
                  boxShadow: active
                    ? (isLight ? `0 0 10px ${activeBg}55, inset 0 0 6px ${activeBg}33` : `0 0 14px ${tt.color}88, inset 0 0 8px ${tt.color}44`)
                    : "none",
                  opacity: 1,
                  visibility: "visible",
                }}
                className="flex items-center gap-2 px-4 py-3 font-mono text-[15px] font-bold uppercase tracking-wider transition-colors"
                data-testid={`tab-${tt.id}`}
                aria-expanded={isStandardsToggle ? showStandardsRow : undefined}>
                <Icon
                  className="w-5 h-5"
                  style={{
                    color: iconColor,
                    filter: !isLight && !active ? `drop-shadow(0 0 4px ${baseColor}55)` : "none",
                  }}
                />
                {tt.id === "rd" ? (
                  <span
                    className="font-bold tracking-wider"
                    style={{ color: textColor, fontWeight: 700, opacity: 1 }}>
                    R&amp;D Lab
                  </span>
                ) : (
                  <span className="font-bold" style={{ color: textColor, fontWeight: 700, opacity: 1 }}>
                    {t(tt.labelKey, tt.id)}
                  </span>
                )}
              </button>
            );
          })}

          {/* ── Row 2 · Right-edge "Digital Constitution / Technical Whitepaper" CTA ── */}
          <Link
            to="/whitepaper"
            data-testid="tab-whitepaper-cta"
            title="Intelligent Systems Integration in Thermal Engineering Analytics"
            className="group"
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 16px",
              marginRight: 10,
              borderRadius: 9999,
              background: (conditions?._mode === "day")
                ? "linear-gradient(135deg, #F59E0B, #FBBF24)"
                : "linear-gradient(135deg, rgba(245,158,11,0.18), rgba(251,191,36,0.08))",
              color: (conditions?._mode === "day") ? "#0A0A0F" : "#FBBF24",
              textDecoration: "none",
              fontFamily: "'Inter', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              border: (conditions?._mode === "day")
                ? "1px solid rgba(245,158,11,0.55)"
                : "1px solid rgba(245,158,11,0.5)",
              boxShadow: (conditions?._mode === "day")
                ? "0 0 14px rgba(245,158,11,0.45), inset 0 0 8px rgba(255,255,255,0.25)"
                : "0 0 14px rgba(245,158,11,0.35), inset 0 0 6px rgba(245,158,11,0.18)",
              whiteSpace: "nowrap",
              transition: "transform 180ms ease, box-shadow 180ms ease, filter 180ms ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.filter = "brightness(1.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.filter = "brightness(1)";
            }}>
            <BookOpen size={14} style={{ flexShrink: 0 }}/>
            <span>
              {t("portal.whitepaper.cta", "Digital Constitution")}
            </span>
            <span style={{
              fontSize: 9,
              fontWeight: 700,
              padding: "2px 7px",
              borderRadius: 9999,
              background: (conditions?._mode === "day") ? "rgba(10,10,15,0.22)" : "rgba(245,158,11,0.22)",
              color: (conditions?._mode === "day") ? "#0A0A0F" : "#FBBF24",
              letterSpacing: "0.1em",
            }}>
              PDF
            </span>
          </Link>
        </div>

        {/* ── Row 3 · Technical Standards toggle · VERTICAL STACK (alt alta) ── */}
        <div
          data-testid="standards-row3"
          style={{
            maxHeight: showStandardsRow ? "600px" : "0px",
            opacity: showStandardsRow ? 1 : 0,
            transform: showStandardsRow ? "translateY(0)" : "translateY(-4px)",
            overflow: "hidden",
            transition: "max-height 320ms ease-in-out, opacity 220ms ease-in-out, transform 220ms ease-in-out",
            marginTop: showStandardsRow ? 8 : 0,
          }}>
          <div className="border-2 border-[#1F1F1F] bg-[#050505] px-4 py-4 flex flex-col items-stretch gap-2"
            style={{ boxShadow: showStandardsRow ? "0 0 14px #00E5FF22, inset 0 0 8px #00E5FF11" : "none", maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>
            {STANDARDS_ROW3.map((s) => {
              const isInternal = s.id === "TR" || s.internalTab;
              const commonStyle = {
                borderColor: s.color,
                color: s.color,
                textShadow: `0 0 6px ${s.color}55`,
                boxShadow: `0 0 6px ${s.color}22`,
              };
              const onEnter = (e) => {
                e.currentTarget.style.boxShadow = `0 0 16px ${s.color}AA, inset 0 0 8px ${s.color}33`;
                e.currentTarget.style.textShadow = `0 0 10px ${s.color}, 0 0 18px ${s.color}88`;
              };
              const onLeave = (e) => {
                e.currentTarget.style.boxShadow = `0 0 6px ${s.color}22`;
                e.currentTarget.style.textShadow = `0 0 6px ${s.color}55`;
              };
              return isInternal ? (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    if (s.id === "TR") setTab("ts825");
                    else if (s.internalTab) setTab(s.internalTab);
                    setShowStandardsRow(false);
                  }}
                  data-testid={`row3-std-${s.id}`}
                  className="w-full px-4 py-2.5 border-2 bg-black font-mono text-[13px] font-extrabold uppercase tracking-wider transition-all text-center"
                  style={commonStyle}
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}>
                  {s.id === "TR" ? `Open spec PDF ${s.label}` : s.label}
                </button>
              ) : (
                <a
                  key={s.id}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`row3-std-${s.id}`}
                  className="w-full px-4 py-2.5 border-2 bg-black font-mono text-[13px] font-extrabold uppercase tracking-wider transition-all text-center"
                  style={commonStyle}
                  onMouseEnter={onEnter}
                  onMouseLeave={onLeave}>
                  Open spec PDF {s.label}
                </a>
              );
            })}
          </div>
        </div>

        <div className="mt-4"/>

        {tab === "calc" && (
          <div
            className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-px items-start [&>section>*:first-child]:mt-0"
            style={{
              background:  conditions?._mode === "day" ? "#E2E8F0" : "#1F1F1F",
              border: `1px solid ${conditions?._mode === "day" ? "#E2E8F0" : "#1F1F1F"}`,
            }}>
            <section
              className="lg:col-span-5 p-5 sm:p-6 space-y-5"
              style={{ background: conditions?._mode === "day" ? "#FFFFFF" : "#000000" }}
              data-testid="left-panel">
              <LayerBuilder layers={layers} setLayers={setLayers} materials={materials} conditions={conditions} dark={conditions?._mode !== "day"}/>
              <MaterialLibrary
                materials={materials}
                onUse={(m) => setLayers((p) => [...p, {
                  id: crypto.randomUUID(), name: m.name,
                  thickness: 0.05, lambda_value: m.lambda_value, mu: m.mu ?? 1, brand: "",
                }])}
                reload={loadMaterials} conditions={conditions} dark={conditions?._mode !== "day"}/>
            </section>

            <section
              className="lg:col-span-4 p-5 sm:p-6 space-y-5"
              style={{ background: conditions?._mode === "day" ? "#FFFFFF" : "#000000" }}
              data-testid="center-panel">
              <MetricsPanel metrics={metrics} conditions={conditions} setConditions={setConditions}
                zone={climate.zone} dark={conditions?._mode !== "day"}/>
              <GlaserChart metrics={metrics} layers={layers} dark={conditions?._mode !== "day"}/>
              <AIAdvicePanel advice={advice} loading={adviceLoading} onRequest={requestAdvice} dark={conditions?._mode !== "day"}/>
            </section>

            <section
              className="lg:col-span-3 p-5 sm:p-6 space-y-5"
              style={{ background: conditions?._mode === "day" ? "#FFFFFF" : "#000000" }}
              data-testid="right-panel">
              <WallSectionDrawing layers={layers} materials={materials} conditions={conditions} dark={conditions?._mode !== "day"}/>
              <ExportBar project={{project_name:"Thermal Academy Project"}} climate={climate} layers={layers}
                metrics={metrics} conditions={conditions} advice={advice}/>
            </section>
          </div>
        )}

        {tab === "material"  && (
          <div className="mt-6">
            <MaterialDashboardView baseLayers={layers} conditions={conditions} baseMetrics={metrics}/>
          </div>
        )}
        {tab === "ts825"     && <div className="mt-6"><TS825View/></div>}
        {tab === "boyutlar"  && <div className="mt-6"><BoyutlarView conditions={conditions}/></div>}
        {tab === "suppliers" && <div className="mt-6"><InteractiveSuppliersMap/></div>}
        {tab === "library"   && <div className="mt-6"><LibraryView/></div>}
        {tab === "finans"    && <div className="mt-6"><EkonomiView conditions={conditions}/></div>}
        {tab === "maliyet"   && <div className="mt-6"><MaliyetView/></div>}
        {tab === "compare"   && <div className="mt-6"><CompareView materials={materials} hdd={climate.hdd}/></div>}
        {tab === "rd"        && (
          <div className="mt-6 space-y-6" data-tab="rnd" data-testid="arge-lab-view">
            <AcademicView/>
            <section className="border-2 border-[#FF3B30] bg-black"
              style={{ boxShadow: "0 0 14px #FF3B3033" }}
              data-testid="arge-fire-retardants-section">
              <header className="bg-[#FF3B30] text-black px-4 py-2 flex items-center gap-2">
                <Flame className="w-5 h-5"/>
                <span className="font-mono text-sm uppercase tracking-[0.22em] font-extrabold">
                  Fire Retardants · Sub-Module
                </span>
              </header>
              <div className="p-2">
                <FireRetardantsView/>
              </div>
            </section>
          </div>
        )}
        {tab === "glaser"    && <div className="mt-6"><GlaserPage metrics={metrics} layers={layers} conditions={conditions}/></div>}
        {tab === "advisor"   && <div className="mt-6"><SmartAdvisor conditions={conditions}/></div>}
        {tab === "projects"  && <div className="mt-6"><ProjectsPanel getCurrentSnapshot={getCurrentSnapshot} applySnapshot={applySnapshot} conditions={conditions}/></div>}

        {/* Neon footer */}
        <footer className="w-full py-6 text-center mt-10" style={{ backgroundColor: '#000000', borderTop: '1px solid #00E5FF22' }} data-testid="app-footer">
          <p className="font-bold text-sm uppercase tracking-widest" style={{ color: '#00E5FF', textShadow: '0 0 8px #00E5FFCC' }} data-testid="footer-author">
            Dr. Nihayet KOÇYİĞİT
          </p>
          <p className="mt-2 text-xs tracking-[0.32em] uppercase" style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 300, fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="footer-titles">
            Founder&nbsp;&nbsp;|&nbsp;&nbsp;CEO&nbsp;&nbsp;|&nbsp;&nbsp;Chief Visionary
          </p>
        </footer>
      </main>
    </div>
  );
}
